import { InjuryAdmission } from './injuries';
import { GameStateAdmission, parseGameIdentity, type GameStateSession } from './admission';
import { ChannelFilterLink, type ChannelLinkCounters, type ChannelLinkMode } from '../../workers/channelFilterLink';
import { NO_GAMES, type FeedFilterAggregatorTimers, type GameStateFilter } from '../../workers/feedFilter';

export interface GameStateConnectionConfig { baseUrl: string; token: string }
export type GameStateConnectionStatus = 'connecting' | 'baseline' | 'live' | 'disconnected' | 'stopped';
export interface GameStateSocket {
  onopen: (() => void) | null;
  onmessage: ((event: { data: unknown }) => void) | null;
  onerror: (() => void) | null;
  onclose: (() => void) | null;
  send(data: string): void;
  close(): void;
}
export interface GameStateTransportOptions {
  config: GameStateConnectionConfig;
  keys: readonly string[];
  socket: (url: string) => GameStateSocket;
  read: (url: string, init: { headers: Record<string, string>; signal: AbortSignal }) => Promise<unknown>;
  changed: (transport: GameStateTransport) => void;
  now?: () => number;
  visible?: () => boolean;
  random?: () => number;
  /** Send the versioned `gamestate` filter, `{games: <keys>}`, instead of
   *  the bare subscribe + snapshot (odds-gamestate-feed-filters D6; the
   *  VITE_FEED_FILTERS_GAMESTATE flag). Off = today's wire, unchanged. */
  filter?: boolean;
  /** Timers for the filter's ack window and replace limiter (tests). */
  timers?: FeedFilterAggregatorTimers;
}
const object = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const validEpoch = (v: unknown): v is string => typeof v === 'string' && v.trim().length > 0;
const BATCH_CAP = 50;
const POLL_MS = 5_000;
const SNAPSHOT_MS = 20_000; // producer drain budget is 15 seconds
const REST_MS = 10_000;

/** One owner per configured endpoint/scope. A fresh Begin on an explicitly
 * requested socket is the only epoch election; ordinary data cannot elect it.
 * No trading, source-ranking, legacy conversion or timer-based freshness here.
 */
export class GameStateTransport {
  readonly admission = new GameStateAdmission();
  readonly injuries = new InjuryAdmission();
  status: GameStateConnectionStatus = 'stopped';
  error: string | null = null;
  private session: GameStateSession | null = null;
  private connection = 0;
  private socket: GameStateSocket | null = null;
  private stopped = true;
  private requested = false;
  private baselineComplete = false;
  private retryMs = 1_000;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private snapshotTimer: ReturnType<typeof setTimeout> | null = null;
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private readController: AbortController | null = null;
  private keys: string[];
  private scopeVersion = 0;
  private readonly now: () => number;
  /** The `gamestate` filter link (null with the flag off): its own ack,
   *  generation and fence; an ack timeout or a rejected first filter leaves
   *  the socket and the next one runs the channel bare (spec D1a). */
  private readonly link: ChannelFilterLink<GameStateFilter> | null;
  /** The games axis last put on the wire — never empty. While no reader
   *  names a key (the reader grace) the filter on the wire stays as it was. */
  private wireKeys: string[] = [];
  /** Filter generation of the last snapshot Begin admitted on this socket.
   *  On a filtered socket each selection change streams a scoped
   *  replacement burst under its new generation (D4). */
  private beginGeneration: number | null = null;

  constructor(private readonly options: GameStateTransportOptions) {
    const base = new URL(options.config.baseUrl);
    if (!['https:', 'http:'].includes(base.protocol) || base.username || base.password || base.search || base.hash) {
      throw new Error('Invalid GameState endpoint configuration');
    }
    if (!options.config.token.trim()) throw new Error('GameState consumer token is required');
    this.keys = [...new Set(options.keys)].sort();
    if (this.keys.some(key => !parseGameIdentity(key))) throw new Error('GameState requires exact v2 keys');
    this.now = options.now ?? Date.now;
    this.wireKeys = [...this.keys];
    this.link = options.filter ? new ChannelFilterLink<GameStateFilter>({
      channel: 'gamestate',
      send: msg => this.socket?.send(JSON.stringify(msg)),
      // D1a: the normal reconnect path. fail() retires (detaches + closes)
      // this socket first, so nothing it sends afterwards is processed.
      reconnect: () => this.fail('GameState filter not acknowledged; reconnecting unfiltered'),
      legacyOps: () => [{ op: 'subscribe', channel: 'gamestate' }, { op: 'snapshot', channel: 'gamestate' }],
      timers: options.timers,
      now: options.now,
    }) : null;
  }

  /** This socket's filter mode (null with the flag off) — diagnostics. */
  get filterMode(): ChannelLinkMode | null { return this.link?.mode ?? null; }
  /** The installed filter generation on this socket, if any. */
  get filterGeneration(): number | null { return this.link?.fence.pair?.filterGeneration ?? null; }
  /** Drop / expiry counters of the filter link (null with the flag off). */
  get filterCounters(): ChannelLinkCounters | null { return this.link ? { ...this.link.counters } : null; }

  /** The filter for the current interest: the readers' keys, or — while no
   *  reader names one — what is already on the wire (never an empty axis). */
  private filterNow(): GameStateFilter {
    if (this.keys.length) this.wireKeys = [...this.keys];
    return { games: this.wireKeys.length ? [...this.wireKeys] : [NO_GAMES] };
  }

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    this.open();
  }

  stop(): void {
    this.stopped = true;
    this.retire();
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.status = 'stopped';
    this.emit();
  }

  /** Visibility return reuses the serialized poll; never overlaps requests. */
  refresh(): void {
    if (this.pollTimer) clearTimeout(this.pollTimer);
    this.pollTimer = null;
    void this.poll();
  }

  setKeys(keys: readonly string[]): void {
    const next = [...new Set(keys)].sort();
    if (next.some(key => !parseGameIdentity(key))) throw new Error('GameState requires exact v2 keys');
    if (JSON.stringify(next) === JSON.stringify(this.keys)) return;
    this.keys = next;
    this.scopeVersion++;
    // The union moved: replace the filter on the wire (rate-limited by the
    // link). An empty union keeps the current filter — a reader returning
    // inside the grace finds its records still live.
    if (this.link && next.length) this.link.update(this.filterNow());
    this.readController?.abort();
    // Aborting invalidates the old read even if the HTTP client ignores it.
    this.readController = null;
    this.refresh();
  }

  private emit(): void { this.options.changed(this); }

  private retire(): void {
    this.injuries.disconnect();
    this.connection++;
    this.requested = false;
    this.baselineComplete = false;
    this.beginGeneration = null;
    this.link?.closeSocket();
    if (this.snapshotTimer) clearTimeout(this.snapshotTimer);
    if (this.pollTimer) clearTimeout(this.pollTimer);
    this.snapshotTimer = this.pollTimer = null;
    this.readController?.abort();
    this.readController = null;
    if (this.session) this.admission.disconnect(this.session);
    this.session = null;
    const socket = this.socket;
    this.socket = null;
    if (socket) {
      socket.onopen = socket.onmessage = socket.onclose = socket.onerror = null;
      socket.close();
    }
  }

  private fail(message: string): void {
    if (this.stopped) return;
    this.retire();
    this.status = 'disconnected';
    this.error = message;
    this.emit();
    const delay = this.retryMs + Math.floor((this.options.random ?? Math.random)() * this.retryMs * 0.25);
    this.retryTimer = setTimeout(() => { this.retryTimer = null; this.open(); }, delay);
    this.retryMs = Math.min(this.retryMs * 2, 30_000);
  }

  private open(): void {
    if (this.stopped) return;
    const id = ++this.connection;
    this.status = 'connecting';
    this.emit();
    const url = new URL('ws', this.options.config.baseUrl.replace(/\/$/, '') + '/');
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    url.searchParams.set('token', this.options.config.token);
    try {
      const socket = this.options.socket(url.toString());
      this.socket = socket;
      const current = () => !this.stopped && id === this.connection && socket === this.socket;
      socket.onopen = () => {
        if (!current()) return;
        try {
          if (this.link) {
            // Filtered: `{games}` and its ack; the install streams the scoped
            // snapshot by itself (no snapshot op). For one socket after a D1a
            // expiry the link runs the legacy pair below instead.
            this.link.openSocket();
            this.link.subscribe(this.filterNow());
            this.requested = true;
            this.status = 'baseline';
            this.emit();
            return;
          }
          // This legacy gamestate subscribe has no filterAck. A matching
          // snapshot Begin is the observable accepted control boundary.
          socket.send(JSON.stringify({ op: 'subscribe', channel: 'gamestate' }));
          this.requested = true;
          this.status = 'baseline';
          socket.send(JSON.stringify({ op: 'snapshot', channel: 'gamestate' }));
          this.emit();
        } catch { this.fail('GameState subscription failed'); }
      };
      socket.onmessage = event => {
        if (!current()) return;
        // Core sends JSON text. Reject unexpected binary or malformed data
        // without logging a URL, token or opaque server payload.
        if (typeof event.data !== 'string') { this.fail('Invalid GameState wire frame'); return; }
        let frame: unknown;
        try { frame = JSON.parse(event.data); } catch { this.fail('Invalid GameState wire frame'); return; }
        this.message(frame);
      };
      socket.onerror = () => { if (current()) this.fail('GameState connection failed'); };
      socket.onclose = () => { if (current()) this.fail('GameState disconnected'); };
      this.snapshotTimer = setTimeout(() => {
        if (current()) this.fail('GameState snapshot did not complete');
      }, SNAPSHOT_MS);
    } catch { this.fail('GameState connection failed'); }
  }

  private message(frame: unknown): void {
    if (!object(frame)) { this.fail('Invalid GameState wire frame'); return; }
    if (this.link && frame.type === 'filterAck') {
      if (frame.channel === 'gamestate') this.filterAck(frame);
      return;
    }
    if (frame.type === 'error' || (frame.type === 'filterAck' && frame.error)) {
      this.fail('GameState subscription rejected'); return;
    }
    // Injury siblings deliberately omit the score sourceEpoch: their revisions
    // survive restarts. They still require this socket's admitted baseline and
    // exact filter stamp; no sibling may elect a score epoch (#1255).
    if (frame.type === 'gamestate' && frame.kind === 'injuries') {
      if (!this.requested || !this.session || (this.link && !this.link.admits(frame))) return;
      const key = typeof frame.gameKey === 'string' ? frame.gameKey : '';
      if (frame.ticker !== key || !this.keys.includes(key)) return;
      if (this.injuries.accept(key, frame.injuries, this.now(), frame.generation) && this.baselineComplete) this.emit();
      return;
    }
    if (!['gsSnapshotBegin', 'gameState', 'gsSnapshotEnd'].includes(String(frame.type))) return;
    // Filtered socket: the installed pair only; legacy socket (D1a):
    // unstamped only. Anything else is dropped (and counted by the link).
    if (this.link && !this.link.admits(frame)) return;
    if (!this.requested || !validEpoch(frame.sourceEpoch)) {
      this.fail('GameState frame lacks an admitted baseline'); return;
    }
    if (this.session && frame.sourceEpoch !== this.session.epoch) {
      this.fail('GameState source changed; obtaining a new baseline'); return;
    }
    if (frame.type === 'gsSnapshotBegin') {
      const filtered = this.link?.mode === 'filtered';
      if (filtered && this.session && frame.filterGeneration !== this.beginGeneration) {
        // A scoped replacement (D4): a newer filter generation's burst. It
        // supersedes any burst still open from the previous generation.
        this.beginGeneration = frame.filterGeneration as number;
        if (!this.admission.begin(this.session, frame, this.acceptedScope())) this.fail('Invalid GameState snapshot');
        else this.injuries.begin(frame.generation as number, this.keys);
        return;
      }
      if (this.baselineComplete || this.session) { this.fail('Unexpected GameState snapshot'); return; }
      this.session = this.admission.connect(this.options.config.baseUrl, frame.sourceEpoch);
      if (filtered) this.beginGeneration = frame.filterGeneration as number;
      if (!this.admission.begin(this.session, frame, filtered ? this.acceptedScope() : null)) this.fail('Invalid GameState snapshot');
      else this.injuries.begin(frame.generation as number, this.keys);
      return;
    }
    // Live churn can precede Begin. It is not a baseline and cannot elect
    // the epoch, but the explicitly requested snapshot may still arrive.
    if (!this.session) return;
    if (frame.type === 'gameState') {
      const accepted = this.admission.accept(this.session, frame, this.now());
      if (accepted && frame.state === null) this.injuries.accept(String(frame.ticker), null, this.now(), frame.generation);
      if (accepted && this.baselineComplete) this.emit();
      return;
    }
    if (!this.admission.end(this.session, frame, this.now())) {
      this.fail('Invalid or incomplete GameState snapshot'); return;
    }
    if (this.snapshotTimer) clearTimeout(this.snapshotTimer);
    this.snapshotTimer = null;
    this.injuries.end(new Set([...this.admission.snapshot()].filter(([, row]) => row.state).map(([key]) => key)), this.now());
    this.baselineComplete = true;
    this.retryMs = 1_000;
    this.status = 'live';
    this.error = null;
    this.emit();
    void this.poll();
  }

  /** The ACCEPTED scope (the ack's `effective` echo) for admission's
   *  snapshot-omission rule — never the requested one. */
  private acceptedScope(): { sports: readonly string[] | null; games: readonly string[] | null } | null {
    const scope = this.link?.installedScope;
    if (!scope) return null;
    return { sports: scope.sports ?? null, games: scope.games ?? null };
  }

  private filterAck(frame: Record<string, unknown>): void {
    const res = this.link!.onAck(frame as { socketIncarnation: unknown; filterGeneration: unknown; effective?: GameStateFilter; error?: string });
    // A rejected FIRST filter has already left the socket inside onAck.
    if (res?.kind !== 'installed') return;
    // A selection change after the baseline streams a scoped replacement;
    // bound it like the baseline so a burst that never ends cannot leave
    // admission's staging open (and the comparison poll refused) forever.
    if (this.baselineComplete) {
      if (this.snapshotTimer) clearTimeout(this.snapshotTimer);
      const id = this.connection;
      this.snapshotTimer = setTimeout(() => {
        if (!this.stopped && id === this.connection) this.fail('GameState snapshot did not complete');
      }, SNAPSHOT_MS);
    }
  }

  private schedule(): void {
    if (this.stopped || !this.baselineComplete) return;
    this.pollTimer = setTimeout(() => { this.pollTimer = null; void this.poll(); }, POLL_MS);
  }

  private async poll(): Promise<void> {
    if (this.stopped || !this.baselineComplete || !this.session || this.readController) return;
    if (this.options.visible?.() === false || !this.keys.length) { this.schedule(); return; }
    const id = this.connection;
    const session = this.session;
    const scopeVersion = this.scopeVersion;
    const controller = new AbortController();
    this.readController = controller;
    const timeout = setTimeout(() => controller.abort(), REST_MS);
    const aborted = new Promise<never>((_, reject) => {
      controller.signal.addEventListener('abort', () => reject(new Error('Read aborted')), { once: true });
    });
    try {
      const chunks: string[][] = [];
      for (let i = 0; i < this.keys.length; i += BATCH_CAP) chunks.push(this.keys.slice(i, i + BATCH_CAP));
      const results = await Promise.allSettled(chunks.map(async keys => {
        const ticket = this.admission.startRest(session, keys);
        const injuryTicket = this.injuries.ticket(keys);
        if (!ticket) throw new Error('Read not admitted');
        const url = new URL('api/analytics/game-states', this.options.config.baseUrl.replace(/\/$/, '') + '/');
        url.searchParams.set('gameKeys', keys.join(','));
        const raw = await Promise.race([aborted, this.options.read(url.toString(), {
          headers: { Authorization: `Bearer ${this.options.config.token}` }, signal: controller.signal,
        })]);
        if (this.stopped || id !== this.connection || scopeVersion !== this.scopeVersion || controller.signal.aborted) return;
        if (!object(raw) || !object(raw.states)) throw new Error('Invalid batch');
        const publication = typeof raw.generatedAt === 'string' ? Date.parse(raw.generatedAt) : NaN;
        if (!Number.isFinite(publication) || publication <= 0) throw new Error('Invalid publication');
        const states: Record<string, unknown> = {};
        for (const key of keys) {
          if (!Object.prototype.hasOwnProperty.call(raw.states, key)) throw new Error('Incomplete batch');
          const state = raw.states[key];
          if (object(state) && validEpoch(state.sourceEpoch) && state.sourceEpoch !== session.epoch) {
            // Even REST can discover a rotation; data does not elect it.
            if (id === this.connection && scopeVersion === this.scopeVersion && !controller.signal.aborted) {
              this.fail('GameState source changed; obtaining a new baseline');
            }
            throw new Error('Epoch changed');
          }
          states[key] = state;
        }
        if (!this.admission.finishRest(ticket, { states, serverTs: publication }, this.now())) {
          throw new Error('Comparison not admitted');
        }
        for (const key of keys) {
          const state = states[key];
          this.injuries.rest(key, object(state) ? state.injuries ?? null : null, this.now(), injuryTicket);
        }
        this.emit();
      }));
      if (this.stopped || id !== this.connection || scopeVersion !== this.scopeVersion) return;
      if (controller.signal.aborted) {
        this.error = 'GameState comparison refresh timed out';
        this.emit();
        return;
      }
      this.error = results.some(result => result.status === 'rejected') ? 'GameState comparison refresh failed' : null;
      this.emit();
    } catch {
      if (!this.stopped && id === this.connection && scopeVersion === this.scopeVersion) {
        this.error = 'GameState comparison refresh failed';
        this.emit();
      }
    } finally {
      clearTimeout(timeout);
      if (this.readController === controller) this.readController = null;
      if (!this.stopped && id === this.connection && scopeVersion === this.scopeVersion) this.schedule();
    }
  }
}
